import type { ActionGroup } from "@/actions/action-types";
import { commandPaletteBadgesAtom, commandPaletteGroupsAtom } from "@/actions/command-palette-registry";
import { archiveViewAtom } from "@/atoms/ui-state";
import { commandPaletteFilter } from "@/components/command-palette/command-palette-filter";
import { cn } from "@/components/podkit/lib/cn";

const noop = () => undefined;
import { IconWorkflows } from "@/assets/icons/central/IconAutomations";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/flexkit/Command";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { commandItemIconClass, commandItemIconClassCentered } from "@/components/command-palette/constants";
import { Flags } from "@/feature-flags";
import { useFlag, useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { AutomationsAccessLevel, useFeatureAutomations } from "@/hooks/use-feature-automations";
import { useMembership } from "@/hooks/use-membership";
import { useLogout } from "@/queries/user-queries";
import { TrackLocations } from "@/hooks/use-segment";
import { useTheme } from "@/hooks/use-theme";
import { CreateEnvironmentModalKeyBinding, KeyboardHelpKeyBinding, OpenSettingsPageKeyBinding } from "@/utils/platform";
import { IconArchive } from "central-icons/IconArchive";
import { IconBook } from "central-icons/IconBook";
import { IconBuildings } from "central-icons/IconBuildings";
import { IconDoor } from "central-icons/IconDoor";
import { IconCirclePerson } from "central-icons/IconCirclePerson";
import { IconCirclePlus } from "central-icons/IconCirclePlus";
import { IconHomeOpen } from "central-icons/IconHomeOpen";
import { IconKeyboard } from "central-icons/IconKeyboard";
import { IconLayoutGrid2 } from "central-icons/IconLayoutGrid2";
import { IconMoon } from "central-icons/IconMoon";
import { IconPieChart2 } from "central-icons/IconPieChart2";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";
import { IconSun } from "central-icons/IconSun";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useAtomValue, useSetAtom } from "jotai";
import { ChevronLeft, Monitor } from "lucide-react";
import {
    memo,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AutomationsPage } from "@/components/command-palette/pages/AutomationsPage";
import { EnvironmentPage } from "@/components/command-palette/pages/EnvironmentPage";
import { EnvironmentsPage } from "@/components/command-palette/pages/EnvironmentsPage";
import { OrganizationsPage } from "@/components/command-palette/pages/OrganizationsPage";
import { OrgSettingsPage } from "@/components/command-palette/pages/OrgSettingsPage";
import { ProjectsPage } from "@/components/command-palette/pages/ProjectsPage";
import { UserSettingsPage } from "@/components/command-palette/pages/UserSettingsPage";
import { useDesktop } from "@/hooks/use-desktop";

type CommandPaletteProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreateEnvironment: () => void;
    onCreateProject: () => void;
    onShowKeyboardShortcuts: () => void;
};

import { type Page, pageLabel } from "@/components/command-palette/types";

/**
 * Renders an ActionGroup from the dynamic registry as a CommandGroup.
 * Maps the rendering-agnostic Action type to cmdk's CommandItem.
 */
const DynamicActionGroup: FC<{
    group: ActionGroup;
    runCommand: (fn: () => void) => void;
    runAsyncCommand: (fn: () => Promise<void>) => void;
}> = ({ group, runCommand, runAsyncCommand }) => {
    const visibleActions = group.actions.filter((a) => !a.hidden);
    if (visibleActions.length === 0) return null;

    return (
        <CommandGroup heading={group.heading}>
            {visibleActions.map((action) => (
                <CommandItem
                    key={action.id}
                    value={action.label}
                    keywords={action.keywords}
                    disabled={action.disabled}
                    onSelect={() => {
                        const result = action.onSelect();
                        if (result instanceof Promise) {
                            runAsyncCommand(() => result);
                        } else {
                            // Action already executed synchronously; close the palette.
                            runCommand(noop);
                        }
                    }}
                >
                    {action.icon && <span className={commandItemIconClass}>{action.icon}</span>}
                    <span>{action.label}</span>
                    {action.shortcut && (
                        <CommandShortcut>
                            <KeyboardCombo keys={action.shortcut} />
                        </CommandShortcut>
                    )}
                    {action.trailing}
                </CommandItem>
            ))}
        </CommandGroup>
    );
};

export const CommandPalette: FC<CommandPaletteProps> = memo(function CommandPalette({
    open,
    onOpenChange,
    onCreateEnvironment,
    onCreateProject,
    onShowKeyboardShortcuts,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isDesktop } = useDesktop();
    const { theme, effectiveTheme, setTheme } = useTheme();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { value: isInsightsDisabled } = useFlag(Flags.InsightsDisabled, false);
    const { accessLevel: workflowAccessLevel } = useFeatureAutomations();
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const logout = useLogout();

    const [pages, setPages] = useState<Page[]>([]);
    const [search, setSearch] = useState("");
    // Defer the search value used for conditional rendering so the input
    // stays responsive while the list re-renders in the background.
    const deferredSearch = useDeferredValue(search);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const activePage = pages[pages.length - 1] as Page | undefined;

    // Dynamic action groups registered by pages via useRegisterCommandPaletteActions
    const rawDynamicGroups = useAtomValue(commandPaletteGroupsAtom);
    const dynamicBadges = useAtomValue(commandPaletteBadgesAtom);

    // When searching, reorder dynamic groups so groups with label-matching
    // items appear before groups that only match on keywords.
    const dynamicGroups = useMemo(() => {
        if (!deferredSearch) return rawDynamicGroups;
        const s = deferredSearch.toLowerCase();
        const bestScore = (g: ActionGroup) => {
            if (g.actions.length === 0) return 0;
            return Math.max(...g.actions.map((act) => commandPaletteFilter(act.label, s, act.keywords)));
        };
        return [...rawDynamicGroups].sort((a, b) => bestScore(b) - bestScore(a));
    }, [rawDynamicGroups, deferredSearch]);
    const hasDynamicContent = dynamicGroups.length > 0;

    // When the palette opens, set the initial page based on the current route.
    // On an environment details page, start at that environment's action page.
    const prevOpenRef = useRef(false);
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            // Always start on the root page — environment-specific actions
            // are surfaced via dynamic groups registered by the detail layout.
            setPages([]);
            setSearch("");
        }
        prevOpenRef.current = open;
    }, [open]);

    const close = useCallback(() => {
        onOpenChange(false);
        setPages([]);
        setSearch("");
    }, [onOpenChange]);

    const runCommand = useCallback(
        (fn: () => void) => {
            close();
            fn();
        },
        [close],
    );

    const runAsyncCommand = useCallback(
        (fn: () => Promise<void>) => {
            close();
            void fn();
        },
        [close],
    );

    const pushPage = useCallback((page: Page) => {
        setPages((prev) => [...prev, page]);
        setSearch("");
    }, []);

    const popPage = useCallback(() => {
        setPages((prev) => prev.slice(0, -1));
        setSearch("");
    }, []);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            onOpenChange(nextOpen);
            if (!nextOpen) {
                // Reset state when closing
                setPages([]);
                setSearch("");
            }
        },
        [onOpenChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Backspace" && !search && pages.length > 0) {
                e.preventDefault();
                popPage();
            }
            if (e.key === "Escape") {
                e.preventDefault();
                if (pages.length > 0) {
                    popPage();
                } else {
                    close();
                }
            }
        },
        [search, pages.length, popPage, close],
    );

    const oppositeTheme = effectiveTheme === "dark" ? "light" : "dark";

    // Focus the input when the palette opens.
    // Single rAF is sufficient because Command is always mounted.
    useEffect(() => {
        if (!open) return;
        const rafId = requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(rafId);
    }, [open]);

    // Close on click outside the palette panel
    const panelRef = useRef<HTMLDivElement>(null);
    const handleOverlayClick = useCallback(
        (e: React.MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                handleOpenChange(false);
            }
        },
        [handleOpenChange],
    );

    // Accessibility trade-off: this is a custom div[role=dialog] instead of a Radix Dialog
    // because cmdk's focus management conflicts with Radix's focus trap. As a result:
    // - No focus trap: Tab can escape the palette (mitigated by the retry focus loop above)
    // - No scroll lock on the background
    // - No aria-describedby
    // Track as a follow-up if keyboard-only navigation becomes a priority.
    return (
        <div
            role="dialog"
            aria-label="Command palette"
            aria-modal={open}
            aria-hidden={!open}
            data-track-location={TrackLocations.Command}
            className={
                open
                    ? "pointer-events-auto fixed inset-0 z-50 flex justify-center bg-surface-primary/50 opacity-100 transition-opacity duration-75"
                    : "pointer-events-none fixed inset-0 z-50 flex justify-center opacity-0 transition-opacity duration-75"
            }
            onClick={handleOverlayClick}
            data-tracking-id="command-palette-overlay"
        >
            <div
                ref={panelRef}
                className={cn(
                    "mx-4 h-fit w-full max-w-[640px] overflow-hidden rounded-lg border border-border-base bg-surface-primary shadow-lg",
                    isDesktop ? "mt-1" : "mt-[20vh]",
                )}
                style={open ? undefined : { contentVisibility: "hidden" }}
            >
                <Command
                    loop
                    filter={commandPaletteFilter}
                    onKeyDown={handleKeyDown}
                    className="border-0 shadow-none [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:text-content-muted [&_[cmdk-group]]:p-1.5 [&_[cmdk-input-wrapper]]:px-4 [&_[cmdk-input]]:h-11 [&_[cmdk-input]]:text-base [&_[cmdk-item]]:px-2.5 [&_[cmdk-item]]:py-2 [&_[cmdk-item]]:text-base [&_kbd]:my-0 [&_kbd]:h-4 [&_kbd]:px-1 [&_kbd]:text-[10px] [&_kbd]:leading-4"
                >
                    <div className="flex items-center border-b border-border-base">
                        {activePage && (
                            <button
                                onClick={popPage}
                                className="ml-4 mr-1 flex shrink-0 items-center gap-0.5 rounded bg-surface-accent px-1.5 py-0.5 text-xs text-content-secondary"
                                data-tracking-id="command-palette-pop-page"
                            >
                                <ChevronLeft className="size-3" />
                                {pageLabel(activePage)}
                            </button>
                        )}
                        <CommandInput
                            ref={inputRef}
                            value={search}
                            onValueChange={setSearch}
                            placeholder={
                                activePage
                                    ? `Search ${pageLabel(activePage).toLowerCase()}...`
                                    : "Type a command or search..."
                            }
                            containerClassName="border-0 flex-1"
                            hideIcon
                        />
                    </div>
                    <CommandList
                        ref={listRef}
                        className="max-h-[min(400px,60vh)] scroll-py-1.5 [&_[cmdk-group]]:[content-visibility:auto]"
                    >
                        {open && !activePage && dynamicBadges.map((badge, i) => <span key={i}>{badge}</span>)}
                        {!activePage && <CommandEmpty>No results found.</CommandEmpty>}
                        {/* Dynamic groups first so contextual actions rank above static items */}
                        {open && !activePage && hasDynamicContent && (
                            <>
                                {dynamicGroups.map((group) => (
                                    <DynamicActionGroup
                                        key={group.id}
                                        group={group}
                                        runCommand={runCommand}
                                        runAsyncCommand={runAsyncCommand}
                                    />
                                ))}
                                <CommandSeparator />
                            </>
                        )}
                        {open && !activePage && (
                            <>
                                {deferredSearch.length > 0 && (
                                    <EnvironmentsPage runCommand={runCommand} navigate={navigate} />
                                )}
                                <RootPage
                                    pathname={location.pathname}
                                    runCommand={runCommand}
                                    pushPage={pushPage}
                                    navigate={navigate}
                                    onCreateEnvironment={onCreateEnvironment}
                                    onCreateProject={onCreateProject}
                                    onShowKeyboardShortcuts={onShowKeyboardShortcuts}
                                    onLogout={() => runCommand(() => void logout.mutateAsync())}
                                    theme={theme}
                                    oppositeTheme={oppositeTheme}
                                    setTheme={setTheme}
                                    isOnaEnabled={isOnaEnabled}
                                    isInsightsDisabled={isInsightsDisabled}
                                    isOrgAdmin={isOrgAdmin}
                                    workflowAccessLevel={workflowAccessLevel}
                                />
                            </>
                        )}
                        {open && activePage?.type === "environments" && (
                            <EnvironmentsPage runCommand={runCommand} navigate={navigate} />
                        )}
                        {open && activePage?.type === "environment" && (
                            <EnvironmentPage
                                environmentId={activePage.environmentId}
                                runCommand={runCommand}
                                runAsyncCommand={runAsyncCommand}
                                navigate={navigate}
                            />
                        )}
                        {open && activePage?.type === "projects" && (
                            <ProjectsPage runCommand={runCommand} navigate={navigate} />
                        )}
                        {open && activePage?.type === "automations" && (
                            <AutomationsPage runCommand={runCommand} navigate={navigate} />
                        )}
                        {open && activePage?.type === "user-settings" && <UserSettingsPage runCommand={runCommand} />}
                        {open && activePage?.type === "org-settings" && (
                            <OrgSettingsPage runCommand={runCommand} navigate={navigate} />
                        )}
                        {open && activePage?.type === "organizations" && (
                            <OrganizationsPage runCommand={runCommand} navigate={navigate} />
                        )}
                    </CommandList>
                </Command>
            </div>
        </div>
    );
});

type RootPageProps = {
    pathname: string;
    runCommand: (fn: () => void) => void;
    pushPage: (page: Page) => void;
    navigate: ReturnType<typeof useNavigate>;
    onCreateEnvironment: () => void;
    onCreateProject: () => void;
    onShowKeyboardShortcuts: () => void;
    onLogout: () => void;
    theme: "dark" | "light" | "system";
    oppositeTheme: "dark" | "light";
    setTheme: (theme: "dark" | "light" | "system") => void;
    isOnaEnabled: boolean;
    isInsightsDisabled: boolean;
    isOrgAdmin: boolean;
    workflowAccessLevel: AutomationsAccessLevel;
};

const RootPage = memo<RootPageProps>(function RootPage({
    pathname,
    runCommand,
    pushPage,
    navigate,
    onCreateEnvironment,
    onCreateProject,
    onShowKeyboardShortcuts,
    onLogout,
    theme,
    oppositeTheme,
    setTheme,
    isOnaEnabled,
    isInsightsDisabled,
    isOrgAdmin,
    workflowAccessLevel,
}) {
    // Ref avoids invalidating the entire handler map on theme toggle.
    const oppositeThemeRef = useRef(oppositeTheme);
    oppositeThemeRef.current = oppositeTheme;

    const setArchiveView = useSetAtom(archiveViewAtom);

    const handlers = useMemo(
        () => ({
            createEnvironment: () => runCommand(onCreateEnvironment),
            createProject: () =>
                runCommand(() => {
                    navigate("/projects");
                    onCreateProject();
                }),
            newAutomation: () => runCommand(() => navigate("/automations/new")),
            goHome: () => runCommand(() => navigate("/ai")),
            goProjects: () => runCommand(() => navigate("/projects")),
            goAutomations: () => runCommand(() => navigate("/automations")),
            goInsights: () => runCommand(() => navigate("/insights")),
            goArchive: () =>
                runCommand(() => {
                    navigate("/");
                    setArchiveView(true);
                }),
            searchEnvironments: () => pushPage({ type: "environments" }),
            searchProjects: () => pushPage({ type: "projects" }),
            searchAutomations: () => pushPage({ type: "automations" }),
            userSettings: () => pushPage({ type: "user-settings" }),
            orgSettings: () => pushPage({ type: "org-settings" }),
            switchOrganization: () => pushPage({ type: "organizations" }),
            logout: onLogout,
            systemTheme: () => runCommand(() => setTheme("system")),
            toggleTheme: () => runCommand(() => setTheme(oppositeThemeRef.current)),
            showKeyboardShortcuts: () => runCommand(onShowKeyboardShortcuts),
            openDocs: () => runCommand(() => window.open("https://ona.com/docs", "_blank", "noopener,noreferrer")),
        }),
        [
            navigate,
            onCreateEnvironment,
            onCreateProject,
            onShowKeyboardShortcuts,
            onLogout,
            runCommand,
            pushPage,
            setTheme,
            setArchiveView,
        ],
    );

    const hasAutomations = workflowAccessLevel !== AutomationsAccessLevel.None;

    // Which item IDs are contextual for the current route — shown first without a heading.
    // Ordered list — first entry renders at the top of the palette.
    const contextualOrder = useMemo<string[]>(() => {
        if (pathname === "/" || pathname === "/ai" || pathname.startsWith("/details/")) {
            return ["new-environment", "search-environments"];
        }
        if (pathname.startsWith("/projects")) {
            return ["new-project", "search-projects", "new-environment"];
        }
        if (pathname.startsWith("/automations")) {
            return ["new-automation", "search-automations"];
        }
        if (pathname.startsWith("/settings")) {
            return ["user-settings", "org-settings"];
        }
        return ["new-environment", "search-environments"];
    }, [pathname]);

    const contextualIds = useMemo(() => new Set(contextualOrder), [contextualOrder]);

    type Item = {
        id: string;
        keywords: string[];
        label: string;
        icon: ReactNode;
        onSelect: () => void;
        shortcut?: readonly string[];
        hidden?: boolean;
    };

    const allItems = useMemo<{ category: string; items: Item[] }[]>(
        () => [
            {
                category: "Search",
                items: [
                    {
                        id: "search-environments",
                        keywords: ["find", "environments"],
                        label: "Search environments...",
                        icon: <IconCirclePlus className={commandItemIconClass} />,
                        onSelect: handlers.searchEnvironments,
                    },
                    {
                        id: "search-projects",
                        keywords: ["find", "projects"],
                        label: "Search projects...",
                        icon: <IconLayoutGrid2 className={commandItemIconClass} />,
                        onSelect: handlers.searchProjects,
                    },
                    {
                        id: "search-automations",
                        keywords: ["find", "automations", "workflows"],
                        label: "Search automations...",
                        icon: (
                            <span className={commandItemIconClassCentered}>
                                <IconWorkflows />
                            </span>
                        ),
                        onSelect: handlers.searchAutomations,
                        hidden: !hasAutomations,
                    },
                ],
            },
            {
                category: "Navigate",
                items: [
                    {
                        id: "go-home",
                        keywords: ["home", "dashboard"],
                        label: "Go to Home",
                        icon: <IconHomeOpen className={commandItemIconClass} />,
                        onSelect: handlers.goHome,
                        hidden: !isOnaEnabled || pathname === "/ai",
                    },
                    {
                        id: "go-projects",
                        keywords: ["projects"],
                        label: "Go to Projects",
                        icon: <IconLayoutGrid2 className={commandItemIconClass} />,
                        onSelect: handlers.goProjects,
                        hidden: pathname === "/projects",
                    },
                    {
                        id: "go-automations",
                        keywords: ["automations", "workflows"],
                        label: "Go to Automations",
                        icon: (
                            <span className={commandItemIconClassCentered}>
                                <IconWorkflows />
                            </span>
                        ),
                        onSelect: handlers.goAutomations,
                        hidden: !hasAutomations || pathname.startsWith("/automations"),
                    },
                    {
                        id: "go-insights",
                        keywords: ["insights", "analytics"],
                        label: "Go to Insights",
                        icon: <IconPieChart2 className={commandItemIconClass} />,
                        onSelect: handlers.goInsights,
                        hidden: isInsightsDisabled || !isOrgAdmin || pathname === "/insights",
                    },
                    {
                        id: "go-archive",
                        keywords: ["archive", "archived"],
                        label: "Go to Archive",
                        icon: <IconArchive className={commandItemIconClass} />,
                        onSelect: handlers.goArchive,
                    },
                ],
            },
            {
                category: "Create",
                items: [
                    {
                        id: "new-environment",
                        keywords: ["create", "environment"],
                        label: "New environment",
                        icon: <IconCirclePlus className={commandItemIconClass} />,
                        onSelect: handlers.createEnvironment,
                        shortcut: CreateEnvironmentModalKeyBinding,
                    },
                    {
                        id: "new-project",
                        keywords: ["create", "project"],
                        label: "New project...",
                        icon: <IconLayoutGrid2 className={commandItemIconClass} />,
                        onSelect: handlers.createProject,
                    },
                    {
                        id: "new-automation",
                        keywords: ["create", "automation", "workflow"],
                        label: "New automation...",
                        icon: (
                            <span className={commandItemIconClassCentered}>
                                <IconWorkflows />
                            </span>
                        ),
                        onSelect: handlers.newAutomation,
                        hidden: !hasAutomations,
                    },
                ],
            },
            {
                category: "Settings",
                items: [
                    {
                        id: "user-settings",
                        keywords: ["settings", "preferences", "user", "account"],
                        label: "User settings...",
                        icon: <IconCirclePerson className={commandItemIconClass} />,
                        onSelect: handlers.userSettings,
                        shortcut: OpenSettingsPageKeyBinding,
                    },
                    {
                        id: "org-settings",
                        keywords: ["settings", "organization", "org", "admin"],
                        label: "Organization settings...",
                        icon: <IconSettingsGear2 className={commandItemIconClass} />,
                        onSelect: handlers.orgSettings,
                    },
                ],
            },
            {
                category: "Account",
                items: [
                    {
                        id: "switch-organization",
                        keywords: ["switch", "organization", "org", "workspace", "team"],
                        label: "Switch organization...",
                        icon: <IconBuildings className={commandItemIconClass} />,
                        onSelect: handlers.switchOrganization,
                    },
                    {
                        id: "logout",
                        keywords: ["log out", "sign out", "exit"],
                        label: "Log out",
                        icon: <IconDoor className={commandItemIconClass} />,
                        onSelect: handlers.logout,
                    },
                ],
            },
            {
                category: "Display",
                items: [
                    {
                        id: "system-theme",
                        keywords: ["theme", "display", "system"],
                        label: "Switch to system theme",
                        icon: <Monitor className={commandItemIconClass} />,
                        onSelect: handlers.systemTheme,
                        hidden: theme === "system",
                    },
                    {
                        id: "toggle-theme",
                        keywords: ["theme", "display", "dark", "light"],
                        label: `Switch to ${oppositeTheme} theme`,
                        icon:
                            oppositeTheme === "dark" ? (
                                <IconMoon className={commandItemIconClass} />
                            ) : (
                                <IconSun className={commandItemIconClass} />
                            ),
                        onSelect: handlers.toggleTheme,
                    },
                ],
            },
            {
                category: "Help",
                items: [
                    {
                        id: "keyboard-shortcuts",
                        keywords: ["help", "keyboard", "shortcuts"],
                        label: "Keyboard shortcuts",
                        icon: <IconKeyboard className={commandItemIconClass} />,
                        onSelect: handlers.showKeyboardShortcuts,
                        shortcut: KeyboardHelpKeyBinding,
                    },
                    {
                        id: "open-docs",
                        keywords: ["help", "docs", "documentation"],
                        label: "Open documentation",
                        icon: <IconBook className={commandItemIconClass} />,
                        onSelect: handlers.openDocs,
                    },
                ],
            },
        ],
        [handlers, hasAutomations, isOnaEnabled, isInsightsDisabled, isOrgAdmin, pathname, theme, oppositeTheme],
    );

    // Collect contextual items in the order specified by contextualOrder.
    const contextItems = useMemo(() => {
        const itemMap = new Map(allItems.flatMap((g) => g.items).map((item) => [item.id, item]));
        return contextualOrder
            .map((id) => itemMap.get(id))
            .filter((item): item is Item => item != null && !item.hidden);
    }, [allItems, contextualOrder]);

    const renderItem = useCallback(
        (item: Item) => (
            <CommandItem key={item.id} value={item.label} keywords={item.keywords} onSelect={item.onSelect}>
                {item.icon}
                <span>{item.label}</span>
                {item.shortcut && (
                    <CommandShortcut>
                        <KeyboardCombo keys={[...item.shortcut]} />
                    </CommandShortcut>
                )}
            </CommandItem>
        ),
        [],
    );

    return (
        <>
            {/* Contextual suggestions for the current page — no heading, shown first */}
            {contextItems.length > 0 && <CommandGroup>{contextItems.map(renderItem)}</CommandGroup>}

            {/* Categorized groups — exclude items already shown in context */}
            {allItems.map((group) => {
                const remaining = group.items.filter((item) => !item.hidden && !contextualIds.has(item.id));
                if (remaining.length === 0) return null;
                return (
                    <CommandGroup key={group.category} heading={group.category}>
                        {remaining.map(renderItem)}
                    </CommandGroup>
                );
            })}
        </>
    );
});
